/**
 * Dot pagination for Dawn's slider-component.
 *
 * Dawn's featured-collection ships a "1 / 5" counter and no dots option.
 * Rather than reimplement slider mechanics, this mirrors the state Dawn
 * already maintains: it reads the counter's total to build the dots, and
 * watches the counter's current value to highlight the active one. Clicks
 * scroll the native scroll container, so momentum, keyboard and touch all
 * keep working. If the script fails to load the counter simply remains.
 */
(() => {
  const build = (sliderComponent) => {
    const counter = sliderComponent.querySelector('.slider-counter');
    const slider = sliderComponent.querySelector('.slider');
    if (!counter || !slider || sliderComponent.dataset.ctDots === 'done') return;

    const currentEl = counter.querySelector('.slider-counter--current');
    const totalEl = counter.querySelector('.slider-counter--total');
    const total = parseInt(totalEl?.textContent || '0', 10);
    if (!currentEl || !total || total < 2) return;

    const slides = [...slider.querySelectorAll('.slider__slide')];
    if (!slides.length) return;

    const nav = document.createElement('div');
    nav.className = 'ct-dots';
    nav.setAttribute('role', 'tablist');
    nav.setAttribute('aria-label', 'Choose a slide');

    const dots = slides.slice(0, total).map((slide, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'ct-dots__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Go to slide ${i + 1} of ${total}`);
      dot.addEventListener('click', () => {
        slider.scrollTo({ left: slide.offsetLeft - slider.offsetLeft, behavior: 'smooth' });
      });
      nav.appendChild(dot);
      return dot;
    });

    const sync = () => {
      const index = parseInt(currentEl.textContent, 10) - 1;
      dots.forEach((d, i) => {
        const active = i === index;
        d.classList.toggle('ct-dots__dot--active', active);
        d.setAttribute('aria-selected', active ? 'true' : 'false');
        d.tabIndex = active ? 0 : -1;
      });
    };

    // Dawn rewrites the counter's text on scroll; mirror that rather than
    // duplicating its scroll maths.
    new MutationObserver(sync).observe(currentEl, { childList: true, characterData: true, subtree: true });
    sync();

    counter.insertAdjacentElement('afterend', nav);
    sliderComponent.dataset.ctDots = 'done';
    sliderComponent.classList.add('ct-has-dots');

    moveArrowsToHeading(sliderComponent);
  };

  /**
   * The reference puts the prev/next arrows beside the section heading and
   * leaves the dots alone beneath the slider.
   *
   * The nodes are relocated rather than recreated: Dawn binds its click
   * handlers to these exact elements during element upgrade, and moving a
   * node preserves its listeners. Recreating them, or positioning them with
   * absolute CSS against a variable-height heading, would both be worse.
   * Runs after DOMContentLoaded, so the upgrade has already happened.
   */
  const moveArrowsToHeading = (sliderComponent) => {
    const section = sliderComponent.closest('.collection, .section');
    const heading = section?.querySelector('.title-wrapper-with-link, .title-wrapper');
    const prev = sliderComponent.querySelector('.slider-button--prev');
    const next = sliderComponent.querySelector('.slider-button--next');
    if (!heading || !prev || !next || heading.querySelector('.ct-slider-nav')) return;

    const nav = document.createElement('div');
    nav.className = 'ct-slider-nav';
    nav.append(prev, next);
    heading.appendChild(nav);
    heading.classList.add('ct-has-slider-nav');
  };

  const init = () => document.querySelectorAll('slider-component').forEach(build);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Sections re-render in the theme editor, so rebuild on Shopify's event.
  document.addEventListener('shopify:section:load', init);
})();
